<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      eager
      scrollable
      max-width="1300px"
      content-class="rounded-dialog"
    >
      <v-form ref="PAPForm" @submit.prevent>
        <v-card>
          <v-card-title dark class="dialog-header">
            <div class="dialog-header-content">
              <div class="dialog-title-wrapper">
                <div class="dialog-icon">
                  <icon name="circle-add" />
                </div>

                <div class="dialog-text">
                  <div class="dialog-title">
                    {{
                      action == "Upsert"
                        ? "Add New sub-indicators/specifics to Programs, Activities and Projects (PAP)"
                        : action + "  Programs, Activities and Projects (PAP)"
                    }}
                  </div>

                  <div class="dialog-subtitle">
                    Fill in the required information below
                  </div>
                </div>
              </div>

              <v-btn icon class="close-btn" @click="closeD()">
                <icon name="circle-close" />
              </v-btn>
            </div>
          </v-card-title>

          <v-card-text style="max-height: 700px" class="my-4">
            <v-container fluid>
              <!-- PAP Code -->
              <v-row>
                <v-col cols="12" class="d-flex">
                  <div style="width: 260px">
                    <v-text-field
                      dense
                      outlined
                      readonly
                      v-model="pap_code"
                      label="PAP Code"
                      class="rounded-lg"
                      color="#6DB249"
                    />
                  </div>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    label="Key Performance Indicator"
                    outlined
                    :readonly="action == 'Upsert'"
                    v-model="pap_indicators"
                    dense
                    :rules="[formRules.required]"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    label="Definition and Specifics"
                    outlined
                    v-model="pap_def_specs"
                    :rules="[formRules.required]"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" class="mb-3">
                  <div class="global-list-card mb-4">
                    <div class="global-list-header">
                      <div class="global-title">
                        <v-icon size="18" color="#147452" class="mr-2">
                          mdi-tune-variant
                        </v-icon>
                        Target Configuration
                      </div>

                      <div class="global-subtitle">
                        Set the baseline value, SDP target, and configure
                        whether the target will be distributed.
                      </div>
                    </div>

                    <v-row class="pa-4">
                      <v-col cols="4">
                        <v-text-field
                          v-model="pap_baseline"
                          type="number"
                          outlined
                          required
                          :rules="[formRules.zeroAllowed]"
                          label="Baseline"
                          class="rounded-lg"
                          color="#6DB249"
                        />
                      </v-col>

                      <v-col cols="4">
                        <v-text-field
                          v-model="pap_sdp_target"
                          :rules="[formRules.required]"
                          type="number"
                          outlined
                          required
                          label="SDP Target"
                          class="rounded-lg"
                          color="#6DB249"
                        />
                      </v-col>

                      <v-col cols="4">
                        <div
                          class="metric-card"
                          :class="{ active: isDistributed }"
                        >
                          <v-switch
                            v-model="isDistributed"
                            hide-details
                            class="metric-checkbox"
                            color="#147452"
                          />

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Is Distributed</div>
                              <div class="metric-subtitle">
                                Enable target distribution across planning
                                years.
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div></v-col
                >

                <v-col cols="12" class="mb-3">
                  <!-- <div class="text-subtitle-1 font-weight-medium mb-3">
                    SDP Target Type
                  </div> -->
                  <div class="global-list-card">
                    <div class="global-list-header">
                      <div class="global-title">
                        <v-icon size="18" color="#147452" class="mr-2">
                          mdi-target
                        </v-icon>
                        SDP Target Type
                      </div>

                      <div class="global-subtitle">
                        Select target type to determine how the SDP target will
                        be distributed over the planning period.
                      </div>
                    </div>

                    <v-row class="pa-4">
                      <v-col cols="4">
                        <div
                          class="metric-card h-100"
                          :class="{
                            active: pap_sdp_target_type === 'incremental',
                          }"
                          @click="
                            pap_sdp_target_type = 'incremental';
                            selectTargetType();
                          "
                        >
                          <div
                            class="custom-radio"
                            :class="{
                              active: pap_sdp_target_type === 'incremental',
                            }"
                          >
                            <div class="custom-radio-dot"></div>
                          </div>

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Incremental</div>
                              <div class="metric-subtitle">
                                Target increases progressively over time.
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="4">
                        <div
                          class="metric-card h-100"
                          :class="{
                            active: pap_sdp_target_type === 'commulative',
                          }"
                          @click="
                            pap_sdp_target_type = 'commulative';
                            selectTargetType();
                          "
                        >
                          <div
                            class="custom-radio"
                            :class="{
                              active: pap_sdp_target_type === 'commulative',
                            }"
                          >
                            <div class="custom-radio-dot"></div>
                          </div>

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Commulative</div>
                              <div class="metric-subtitle">
                                Targets accumulate throughout the planning
                                period.
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="4">
                        <div
                          class="metric-card h-100"
                          :class="{
                            active: pap_sdp_target_type === 'consistent',
                          }"
                          @click="
                            pap_sdp_target_type = 'consistent';
                            selectTargetType();
                          "
                        >
                          <div
                            class="custom-radio"
                            :class="{
                              active: pap_sdp_target_type === 'consistent',
                            }"
                          >
                            <div class="custom-radio-dot"></div>
                          </div>

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Consistent</div>
                              <div class="metric-subtitle">
                                The same target is maintained every year.
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
                <v-col cols="12" class="mb-3">
                  <!-- <div class="text-subtitle-1 font-weight-medium mb-3">
                    SDP Target Format
                  </div> -->
                  <div class="global-list-card">
                    <div class="global-list-header">
                      <div class="global-title">
                        <v-icon size="18" color="#147452" class="mr-2">
                          mdi-format-list-numbered
                        </v-icon>
                        SDP Target Format
                      </div>

                      <div class="global-subtitle">
                        Select target format to determine how the SDP target
                        will be expressed.
                      </div>
                    </div>

                    <v-row class="pa-4">
                      <v-col cols="6">
                        <div
                          class="metric-card h-100"
                          :class="{
                            active: pap_sdp_target_format === 'percentage',
                          }"
                          @click="pap_sdp_target_format = 'percentage'"
                        >
                          <div
                            class="custom-radio"
                            :class="{
                              active: pap_sdp_target_format === 'percentage',
                            }"
                          >
                            <div class="custom-radio-dot"></div>
                          </div>

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Percentage</div>
                              <div class="metric-subtitle">
                                Values are expressed as percentages (%).
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>

                      <v-col cols="6">
                        <div
                          class="metric-card h-100"
                          :class="{
                            active: pap_sdp_target_format === 'numeric',
                          }"
                          @click="pap_sdp_target_format = 'numeric'"
                        >
                          <div
                            class="custom-radio"
                            :class="{
                              active: pap_sdp_target_format === 'numeric',
                            }"
                          >
                            <div class="custom-radio-dot"></div>
                          </div>

                          <div class="metric-content">
                            <div>
                              <div class="metric-title">Numeric</div>
                              <div class="metric-subtitle">
                                Values are entered as whole numbers or decimals.
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="global-list-card mb-4">
                    <div
                      class="d-flex justify-space-between align-center flex-wrap"
                    >
                      <!-- Left -->
                      <div class="global-list-header">
                        <div class="global-title">
                          <v-icon size="18" color="#147452" class="mr-2">
                            mdi-chart-timeline-variant
                          </v-icon>
                          Annual Target Distribution
                        </div>

                        <div class="global-subtitle">
                          Allocate annual targets across each quarter to support
                          effective performance monitoring.
                        </div>
                      </div>

                      <!-- Right -->
                      <div
                        class="d-flex align-center mr-3 mb-2"
                        v-if="pap_sdp_target"
                      >
                        <div class="goal-badge-small mr-2">
                          <v-icon color="white" style="font-size: 16px">
                            mdi-checkbox-marked-circle-outline
                          </v-icon>
                        </div>

                        <div class="metric-content">
                          <div class="metric-title">Total Tagged Targets</div>

                          <div class="metric-value">
                            {{
                              isTargetSameDistribution == true
                                ? pap_sdp_target
                                : getTotalTaggedTargets
                            }}
                            {{
                              pap_sdp_target_format == "percentage" ? "%" : ""
                            }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Same Distribution -->
                    <div class="mx-2 mb-2 mt-2">
                      <div
                        v-if="pap_sdp_target_type == 'consistent'"
                        class="metric-card py-2"
                        style="min-width: 270px"
                      >
                        <v-switch
                          v-model="isSameTargetDistribution"
                          hide-details
                          class="metric-checkbox"
                          color="#147452"
                          @change="setTargetDistribution()"
                        />

                        <div class="metric-content">
                          <div>
                            <div class="metric-title">
                              Same Target Distribution
                            </div>

                            <div class="metric-subtitle">
                              Apply identical targets to all years.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mx-2 mb-2 mt-2">
                      <v-card class="dt-container" elevation="0" outlined>
                        <v-data-table
                          :headers="target_yr_dist_headers"
                          :items="target_yr_dist_data"
                          hide-default-footer
                        >
                          <template
                            v-slot:[`item.budget_requirement`]="{ item }"
                          >
                            {{
                              item.budget_requirement
                                ? formatPrice(item.budget_requirement)
                                : ""
                            }}
                          </template>

                          <template v-slot:[`item.tagged`]="{ item }">
                            <div
                              class="text-caption"
                              v-for="(i, idx) in item.tagged"
                              :key="idx"
                            >
                              <div class="d-flex justify-space-between">
                                <div
                                  style="width: 90%"
                                  class="d-flex justify-start"
                                >
                                  {{ i.head_user.office }}
                                </div>
                                <div>
                                  ({{ i.target
                                  }}{{
                                    pap_sdp_target_format == "percentage"
                                      ? "%"
                                      : ""
                                  }})
                                </div>
                              </div>
                            </div>
                          </template>

                          <template v-slot:[`item.t_target`]="{ item }">
                            <div v-if="item.tagged.length > 0">
                              {{ getTargetTotal(item.tagged)
                              }}{{
                                pap_sdp_target_format == "percentage" ? "%" : ""
                              }}
                            </div>
                          </template>

                          <template v-slot:[`item.action`]="{ item, index }">
                            <v-btn
                              v-if="pap_sdp_target_type != 'consistent'"
                              class="global-btn-save"
                              style="width: 10px"
                              @click="edit_target(item, index)"
                              >Select</v-btn
                            >
                            <!-- <v-btn
                          v-if="action == 'Update'"
                          x-small
                          class="mt-1"
                          @click="deleteItem(item)"
                          outlined
                          color="red"
                          block
                          >Delete</v-btn
                        > -->
                          </template>
                        </v-data-table>
                      </v-card>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" class="pt-2 px-4"> </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-actions class="pa-5">
            <v-spacer></v-spacer>

            <v-btn class="btn-cancel" outlined @click="closeD()">
              Cancel
            </v-btn>

            <v-btn
              v-if="action == 'Add'"
              class="btn-save"
              @click="openConfirmation()"
              :disabled="isSubmitLoading"
              :loading="isSubmitLoading"
            >
              Save
            </v-btn>
            <v-btn
              v-if="action == 'Upsert'"
              class="btn-save"
              @click="openConfirmation()"
              :disabled="isSubmitLoading"
              :loading="isSubmitLoading"
            >
              Save
            </v-btn>
            <v-btn
              v-if="action == 'Update'"
              class="btn-save"
              @click="openConfirmation()"
              :disabled="isSubmitLoading"
              :loading="isSubmitLoading"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- annual target distribution -->
    <v-dialog
      v-model="targetDialog"
      eager
      persistent
      scrollable
      max-width="1000px"
      content-class="rounded-dialog"
    >
      <v-card>
        <v-card-title dark class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-wrapper">
              <div class="dialog-icon">
                <icon name="circle-add" />
              </div>

              <div class="dialog-text">
                <div class="dialog-title">
                  Set Annual Target Distribution and Budget Requirement
                </div>

                <div class="dialog-subtitle">
                  Fill in the required information below
                </div>
              </div>
            </div>

            <v-btn icon class="close-btn" @click="closeTargetDialog()">
              <icon name="circle-close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text style="max-height: 700px" class="my-4">
          <v-form ref="TargetDialogRef" @submit.prevent>
            <v-container>
              <v-row>
                <!-- sameTargetDistYearList -->
                <v-col class="">
                  <div
                    style="border: 1px solid grey"
                    class="rounded-lg pa-2"
                    v-if="isSameTargetDistribution"
                  >
                    <div class="text-caption">
                      Years with Same Target Distribution
                    </div>
                    <div class="pa-2 d-flex justify-space-around">
                      <v-checkbox
                        v-for="i in sameTargetDistYearList"
                        :key="i.year"
                        v-model="i.checked"
                        :label="`${i.year}`"
                        color="#6DB249"
                        :checked="i.checked"
                      ></v-checkbox>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    label="Success Indicator (Target+Measure)"
                    outlined
                    v-model="target_success_indicator"
                    dense
                    :rules="[formRules.required]"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    @input="handleTargetBudgetRequirement"
                    :value="budget_requirement_formatted"
                    outlined
                    required
                    label="Budget Requirement"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <div class="global-list-card">
                    <div class="d-flex justify-space-between">
                      <div class="global-list-header">
                        <div class="global-title">
                          <v-icon size="18" color="#147452" class="mr-2">
                            mdi-account-group-outline
                          </v-icon>
                          Tagged Heads/Offices
                        </div>

                        <div class="global-subtitle">
                          Manage and select heads/offices to be tagged for the
                          annual target distribution.
                        </div>
                      </div>

                      <div
                        class="text-subtitle-1 mb-2"
                        v-if="pap_sdp_target_type == 'consistent'"
                      >
                        Total Tagged Target:
                        <span
                          ><b
                            >{{
                              isTargetSameDistribution == true
                                ? pap_sdp_target
                                : getConsitentTotalTargets.total
                            }}{{
                              pap_sdp_target_format == "percentage" ? "%" : ""
                            }}</b
                          ></span
                        >
                        <span v-if="pap_sdp_target_type == 'consistent'">
                          <div
                            v-if="pap_sdp_target_type == 'consistent'"
                            class="metric-card py-2 mr-3"
                            style="min-width: 70px"
                          >
                            <v-switch
                              v-model="isTargetSameDistribution"
                              hide-details
                              class="metric-checkbox"
                              color="#147452"
                            />

                            <div class="metric-content">
                              <div>
                                <div class="metric-title">
                                  Same Target Tagging
                                </div>
                              </div>
                            </div>
                          </div></span
                        >
                      </div>
                    </div>
                    <div class="mx-4 mt-3">
                      <div class="d-flex align-center justify-space-between">
                        <div
                          v-if="pap_sdp_target_type != 'consistent'"
                          class="baseline-card"
                        >
                          <span class="baseline-label">Baseline:</span>

                          <span class="baseline-value">
                            {{ getPrevTargets() }}
                            <span
                              v-if="
                                !isNaN(getPrevTargets()) &&
                                  pap_sdp_target_format == 'percentage'
                              "
                            >
                              %
                            </span>
                          </span>
                        </div>

                        <v-btn class="btn-save" @click="add_head_tagging()">
                          Add
                        </v-btn>
                      </div>
                    </div>
                    <div class="mx-2 mb-2 mt-2">
                      <v-card class="dt-container" elevation="0" outlined>
                        <v-data-table
                          :headers="tagged_head_headers"
                          :items="tagged_head_data"
                          hide-default-footer
                        >
                          <template v-slot:[`item.target`]="{ item }">
                            <div>
                              {{ item.target
                              }}{{
                                pap_sdp_target_format == "percentage" ? "%" : ""
                              }}
                            </div>
                          </template>

                          <template v-slot:[`item.action`]="{ item, index }">
                            <div class="button-group pa-2">
                              <v-btn
                                class="global-btn-save"
                                style="width: 10px"
                                @click="edit_tag(item, index)"
                                >Edit</v-btn
                              >
                              <v-btn
                                class="global-btn-save"
                                style="width: 10px"
                                @click="untag(item)"
                                >Untag</v-btn
                              >
                            </div>
                          </template>
                        </v-data-table>
                      </v-card>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-5">
          <v-spacer></v-spacer>
          <v-btn class="btn-cancel" outlined @click="closeTargetDialog()">
            Cancel
          </v-btn>

          <v-btn
            class="btn-save"
            v-if="targetInnerAction == 'Update'"
            @click="saveTarget()"
            :loading="targetBtnLoading"
            :disabled="targetBtnLoading"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- heads users dialog-->
    <v-dialog
      v-model="headsDialog"
      eager
      persistent
      scrollable
      max-width="500px"
      content-class="rounded-dialog"
    >
      <v-card>
        <v-card-title dark class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-title-wrapper">
              <div class="dialog-icon">
                <icon name="circle-add" />
              </div>

              <div class="dialog-text">
                <div class="dialog-title">Select Head/Office</div>

                <div class="dialog-subtitle">
                  Fill in the required information below
                </div>
              </div>
            </div>

            <v-btn icon class="close-btn" @click="closeHeadsDialog()">
              <icon name="circle-close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text style="max-height: 600px" class="my-4">
          <v-form ref="HeadsDialogRef" @submit.prevent>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    v-model="tagged_head_temp_data"
                    :closable-chips="isDistributed"
                    :readonly="!isDistributed"
                    label="Head of Office"
                    outlined
                    class="rounded-lg"
                    item-text="name"
                    item-value="usID"
                    color="#93CB5B"
                    :items="usersList"
                  >
                    <template v-slot:selection="data">
                      <v-chip
                        v-bind="data.attrs"
                        :input-value="data.selected"
                        :close="isDistributed"
                        small
                        @click="data.select"
                        @click:close="
                          isDistributed ? removeTaggedOnClick(data.item) : ''
                        "
                      >
                        {{ data.item.name }}
                      </v-chip>
                    </template>
                    <template v-slot:item="data">
                      <template>
                        <v-list-item-content>
                          <v-list-item-title
                            v-html="data.item.name"
                          ></v-list-item-title>
                          <v-list-item-subtitle
                            v-html="data.item.office"
                          ></v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="head_target"
                    type="number"
                    :rules="[formRules.required]"
                    outlined
                    required
                    label="Target"
                    class="rounded-lg"
                    color="#6DB249"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-5">
          <v-spacer></v-spacer>
          <v-btn class="btn-cancel" outlined @click="closeHeadsDialog()">
            Cancel
          </v-btn>
          <v-btn
            class="btn-save"
            v-if="tagHeadAction == 'Add'"
            :loading="targetBtnLoading"
            :disabled="targetBtnLoading"
            @click="saveTaggedHeads()"
          >
            Add
          </v-btn>
          <v-btn
            class="btn-save"
            v-if="tagHeadAction == 'Update'"
            @click="updateTaggedHeads()"
            :loading="targetBtnLoading"
            :disabled="targetBtnLoading"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- untag dialog -->
    <v-dialog
      v-if="unTagDialog"
      v-model="unTagDialog"
      persistent
      max-width="420"
      content-class="delete-dialog-content"
    >
      <v-card class="delete-dialog">
        <div class="delete-content">
          <div class="warning-icon"><icon name="question" /></div>

          <div class="delete-title">Untag User?</div>

          <div class="delete-description">
            Are you sure you want to untag
            <strong>{{ unTagUser.head_user.name }}</strong
            >?
          </div>
        </div>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            :disabled="confirmBtnLoading"
            :loading="confirmBtnLoading"
            class="btn-cancel"
            outlined
            @click="cancelUntag()"
          >
            Close
          </v-btn>

          <v-spacer />

          <v-btn
            color="error"
            depressed
            class="btn-save"
            :loading="confirmBtnLoading"
            :disabled="confirmBtnLoading"
            @click="confirmUntag()"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmDialog"
      persistent
      max-width="420"
      content-class="delete-dialog-content"
    >
      <v-card class="delete-dialog">
        <div class="delete-content">
          <div class="submit-icon">
            <icon name="circle-check" />
          </div>

          <div class="delete-title">
            {{
              action == "Add"
                ? "Save Changes?"
                : action == "Update"
                ? "Update Changes?"
                : "Add New SDP?"
            }}
          </div>

          <div class="delete-description">
            Are you sure you want to
            <strong>
              {{
                action == "Add"
                  ? "save this record"
                  : action == "Update"
                  ? "update these changes"
                  : "add this new SDP"
              }}
            </strong>
            <span v-if="action == 'Update'">
              ? This action is irreversible.
            </span>
            <span v-else>?</span>
          </div>
        </div>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-btn
            text
            color="grey darken-1"
            class="btn-cancel"
            @click="confirmDialog = false"
          >
            Cancel
          </v-btn>

          <v-spacer />

          <v-btn
            color="#147452"
            depressed
            class="btn-save"
            @click="
              action == 'Add'
                ? addnew()
                : action == 'Update'
                ? update()
                : upsert()
            "
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
      :timeout="4000"
    ></fade-away-message-component>
  </div>
</template>

<script>
import icon from "@/assets/icon.vue";
export default {
  components: {
    icon,
  },
  props: {
    data: null,
    action: null,
  },

  data() {
    return {
      id: null,
      spID: null,
      headsDialog: false,
      confirmDialog: false,
      unTagDialog: false,
      unTagUser: null,
      untagList: [],
      target_accomplishment: null,
      linked: null,
      dialog: false,
      confirmBtnLoading: false,
      target_yr_dist_data: [],
      target_yr_dist_headers: [
        {
          text: "Year",
          value: "year",
          align: "start",
          valign: "start",
          sortable: false,
        },

        {
          text: "Target Distribution Description",
          value: "target_distribution",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Budgetary Requirement",
          value: "budget_requirement",
          align: "center",
          width: 200,
          valign: "center",
          sortable: false,
        },
        {
          text: "Office",
          value: "tagged",
          align: "center",
          valign: "center",
          sortable: false,
        },

        {
          text: "Total Targets",
          value: "t_target",
          align: "center",
          valign: "center",
          sortable: false,
        },

        {
          text: "Action",
          value: "action",
          align: "center",
          valign: "center",
          width: 100,
        },
      ],
      tagged_head_temp_data: [],
      tagged_head_data: [],
      head_target: null,
      tagged_head_headers: [
        {
          text: "Name",
          value: "head_user.name",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Office",
          value: "head_user.office",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Targets",
          value: "target",
          align: "start",
          valign: "start",
          sortable: false,
        },
        {
          text: "Action",
          value: "action",
          align: "center",
          valign: "center",
          width: 200,
        },
      ],
      usersList: [],
      yrSelected: null,
      targetDialog: false,
      target_desc: null,
      target_budget_requirement: null,
      target_distribution: null,
      target_success_indicator: null,
      target_val: null,
      targetBtnLoading: false,
      targetInnerAction: null,
      tagHeadAction: null,
      isSubmitLoading: false,
      pap_code: null,
      pap_indicators: null,
      pap_def_specs: null,
      pap_baseline: null,
      pap_sdp_target: null,
      pap_sdp_target_type: null,
      pap_sdp_target_format: null,
      isSameTargetDistribution: null,
      isTargetSameDistribution: null,
      isDistributed: true,
      sameTargetDistYearList: [],
      prevIndex: null,
      tagIdxToUpdate: null,
      vpID: null,
      fadeAwayMessage: {
        show: false,
        type: "success",
        header: "Successfully Added!",
        message: "",
        top: 10,
      },
      tempHeadsList: [],
    };
  },
  watch: {
    data: {
      handler(data) {
        this.dialog = true;
        // this.getHeadUsers();
        this.getVpAssignedByGoalID(data.goalID);

        this.untagList = [];
        console.log("Data1", data);
        this.target_yr_dist_data = [];
        if (this.action == "Add") {
          this.loadYearly(data.strat_planID);
          this.pap_code = data.goalOrderID + "PAP " + data.strategyCode + "-x";
          this.id = null;
          this.pap_indicators = null;
          this.pap_def_specs = null;
          this.pap_baseline = null;
          this.pap_sdp_target = null;
          this.pap_sdp_target_type = "incremental";
          this.pap_sdp_target_format = "numeric";
          this.isSameTargetDistribution = false;
          this.isTargetSameDistribution = false;
          this.isDistributed = true;
        } else if (this.action == "Upsert") {
          this.loadYearly(data.strat_planID);
          this.pap_code = data.code;
          this.id = data.id;
          this.pap_indicators = data.indicators;
          this.pap_def_specs = null;
          this.pap_baseline = null;
          this.pap_sdp_target = null;
          this.pap_sdp_target_type = "incremental";
          this.pap_sdp_target_format = "numeric";
          this.isSameTargetDistribution = false;
          this.isTargetSameDistribution = false;
          this.isDistributed = true;
        } else {
          console.log("PAP UPDATE", data);
          this.loadYearlyTagged(data.id);
          this.pap_code = data.code;
          this.id = data.id;
          this.pap_indicators = data.indicators;
          this.pap_def_specs = data.def_specs;
          this.pap_baseline = Number(data.baseline);
          this.pap_sdp_target = data.sdp_target;
          this.pap_sdp_target_type = data.sdp_target_type;
          this.pap_sdp_target_format = data.sdp_target_format;
          this.isSameTargetDistribution = data.isSameTargetDistribution;
          this.isTargetSameDistribution = data.isTargetSameDistribution;
          this.isDistributed = data.isDistributed;
        }
      },

      deep: true,
    },
  },

  computed: {
    budget_requirement_formatted() {
      if (this.target_budget_requirement) {
        let qty = this.target_budget_requirement + "";
        return qty.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      return null;
    },
    getConsitentTotalTargets() {
      let total = 0;
      if (this.pap_sdp_target_type == "consistent") {
        for (let i = 0; i < this.tagged_head_data.length; i++) {
          total += parseInt(this.tagged_head_data[i].target);
        }
      }
      return {
        fraction: total + "/" + this.pap_sdp_target,
        total: total,
      };
    },
    getTotalTaggedTargets() {
      let total = 0;
      let toRet;
      if (this.pap_sdp_target_type == "consistent") {
        for (let i = 0; i < this.target_yr_dist_data.length; i++) {
          for (let j = 0; j < this.target_yr_dist_data[i].tagged.length; j++) {
            total += parseInt(this.target_yr_dist_data[i].tagged[j].target);
          }

          if (this.target_yr_dist_data[i].tagged.length > 0) {
            break;
          }
        }

        toRet = total;
      } else if (this.pap_sdp_target_type == "incremental") {
        toRet = this.pap_sdp_target;
      } else {
        for (let i = 0; i < this.target_yr_dist_data.length; i++) {
          for (let j = 0; j < this.target_yr_dist_data[i].tagged.length; j++) {
            total += parseInt(this.target_yr_dist_data[i].tagged[j].target);
          }
        }
        toRet = total + "/" + this.pap_sdp_target;
      }
      return toRet;
    },
  },

  methods: {
    initialize() {},

    handleTargetBudgetRequirement(val) {
      //Make sure the stored qty is not formatted
      this.target_budget_requirement = val.toString().replace(/\D/g, "");
    },

    openConfirmation() {
      if (this.$refs.PAPForm.validate()) {
        this.confirmDialog = true;
      } else {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.header = "System Message";
        this.fadeAwayMessage.message = "Please fill all required fields!";
      }
    },

    // getTargetData(yr) {
    //   let arr = [];
    //   for (let i = 0; i < this.target_yr_dist_data.length; i++) {
    //     if (this.target_yr_dist_data[i].year == yr) {
    //       arr.push(this.target_yr_dist_data[i]);
    //     }
    //   }
    //   return arr;
    // },

    getTargetTotal(item) {
      if (this.isTargetSameDistribution == true) {
        return this.pap_sdp_target;
      } else if (item.length > 0) {
        let t = 0;

        for (let i = 0; i < item.length; i++) {
          t += parseInt(item[i].target);
        }

        return t;
      }
    },

    closeD() {
      this.eventHub.$emit("closeStratPlanTargets", false);
      this.isSubmitLoading = false;
      this.dialog = false;
    },

    selectTargetType() {
      this.isSameTargetDistribution = false;
      for (let i = 0; i < this.target_yr_dist_data.length; i++) {
        this.target_yr_dist_data[i].target_distribution = null;
        this.target_yr_dist_data[i].budget_requirement = null;
        this.target_yr_dist_data[i].tagged = [];
      }
    },

    getPrevTargets() {
      if (
        this.pap_sdp_target_type != "consistent" &&
        this.pap_sdp_target_type == "incremental"
      ) {
        if (this.prevIndex == 0) {
          return this.pap_baseline ? this.pap_baseline : "";
        } else {
          if (this.prevIndex) {
            let data = this.target_yr_dist_data[this.prevIndex - 1].tagged;
            let t = 0;
            if (
              this.target_yr_dist_data[this.prevIndex - 1].target_distribution
            ) {
              for (let i = 0; i < data.length; i++) {
                t += parseInt(data[i].target);
              }
              return t;
            } else {
              return this.pap_baseline ? this.pap_baseline : "";
            }
          }
        }
      } else if (this.pap_sdp_target_type == "commulative") {
        if (this.prevIndex == 0) {
          return this.pap_baseline ? this.pap_baseline : "";
        } else {
          let toReturn = 0;
          if (this.prevIndex) {
            let prev = this.prevIndex;
            while (prev != 0) {
              let data = this.target_yr_dist_data[prev - 1].tagged;
              let t = 0;

              for (let i = 0; i < data.length; i++) {
                t += parseInt(data[i].target);
              }
              toReturn += t;

              prev--;
            }
          }
          return toReturn;
        }
      }
    },

    add_target() {
      this.targetInnerAction = "Add";
      this.targetDialog = true;
    },

    edit_target(data, index) {
      this.prevIndex = index;
      // console.log(
      //   this.pap_sdp_target,
      //   this.pap_baseline,
      //   this.pap_sdp_target != null && this.pap_sdp_target != "",
      //   this.pap_baseline != null,
      //   this.pap_baseline != ""
      // );
      if (
        (this.pap_sdp_target != null || this.pap_sdp_target != "") &&
        (this.pap_baseline != null || this.pap_baseline != "")
      ) {
        this.getHeadUsers();
        this.yrSelected = data.year;
        this.target_distribution = data.target_distribution;
        this.target_budget_requirement = data.budget_requirement;
        this.target_success_indicator = data.success_indicator;
        this.targetInnerAction = "Update";
        this.tagged_head_data = data.tagged.length > 0 ? data.tagged : [];
        this.targetDialog = true;
      } else {
        this.fadeAwayMessage.show = true;
        this.fadeAwayMessage.type = "error";
        this.fadeAwayMessage.header = "System Message";
        this.fadeAwayMessage.message =
          "Please provide Baseline and SDP Target!";
      }
    },

    saveTarget() {
      this.targetBtnLoading = true;
      if (this.$refs.TargetDialogRef.validate()) {
        if (
          this.isSameTargetDistribution &&
          this.pap_sdp_target_type == "consistent"
        ) {
          let taggedTotal = 0;
          for (let i = 0; i < this.tagged_head_data.length; i++) {
            taggedTotal += parseInt(this.tagged_head_data[i].target);
          }
          if (taggedTotal == this.pap_sdp_target) {
            for (let j = 0; j < this.sameTargetDistYearList.length; j++) {
              const idx = this.target_yr_dist_data.findIndex(
                (x) => x.year === this.sameTargetDistYearList[j].year,
              );
              if (this.sameTargetDistYearList[j].checked) {
                this.target_yr_dist_data[
                  idx
                ].target_distribution = this.target_distribution;

                this.target_yr_dist_data[
                  idx
                ].success_indicator = this.target_success_indicator;

                this.target_yr_dist_data[
                  idx
                ].budget_requirement = this.target_budget_requirement;
                this.target_yr_dist_data[idx].tagged = this.tagged_head_data;
              }
            }
            this.tagged_head_data = [];
            this.yrSelected = null;
            this.closeTargetDialog();
            this.targetBtnLoading = false;
          } else if (
            this.isTargetSameDistribution &&
            this.pap_sdp_target_type == "consistent"
          ) {
            for (let j = 0; j < this.sameTargetDistYearList.length; j++) {
              const idx = this.target_yr_dist_data.findIndex(
                (x) => x.year === this.sameTargetDistYearList[j].year,
              );
              if (this.sameTargetDistYearList[j].checked) {
                this.target_yr_dist_data[
                  idx
                ].target_distribution = this.target_distribution;

                this.target_yr_dist_data[
                  idx
                ].success_indicator = this.target_success_indicator;

                this.target_yr_dist_data[
                  idx
                ].budget_requirement = this.target_budget_requirement;
                this.target_yr_dist_data[idx].tagged = this.tagged_head_data;
              }
            }
            this.tagged_head_data = [];
            this.yrSelected = null;
            this.closeTargetDialog();
            this.targetBtnLoading = false;
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message =
              "Tagged Targets is not equal to SDP Target!";
            this.targetBtnLoading = false;
          }
        } else if (this.pap_sdp_target_type == "commulative") {
          for (let i = 0; i < this.target_yr_dist_data.length; i++) {
            if (this.target_yr_dist_data[i].year == this.yrSelected) {
              let target = this.getPrevTargets();

              if (isNaN(target)) {
                this.target_yr_dist_data[
                  i
                ].target_distribution = this.target_distribution;
                this.target_yr_dist_data[
                  i
                ].success_indicator = this.target_success_indicator;
                this.target_yr_dist_data[
                  i
                ].budget_requirement = this.target_budget_requirement;
                this.target_yr_dist_data[i].tagged = this.tagged_head_data;
                this.tagged_head_data = [];
                this.yrSelected = null;
                this.closeTargetDialog();
              } else {
                this.target_yr_dist_data[
                  i
                ].target_distribution = this.target_distribution;
                this.target_yr_dist_data[
                  i
                ].budget_requirement = this.target_budget_requirement;

                this.target_yr_dist_data[
                  i
                ].success_indicator = this.target_success_indicator;

                this.target_yr_dist_data[i].tagged = this.tagged_head_data;

                this.tagged_head_data = [];
                this.yrSelected = null;
                this.closeTargetDialog();
              }
            }
          }
          this.targetBtnLoading = false;
        } else {
          for (let i = 0; i < this.target_yr_dist_data.length; i++) {
            if (this.target_yr_dist_data[i].year == this.yrSelected) {
              let target = this.getPrevTargets();

              if (isNaN(target)) {
                console.log("entered here");
                this.target_yr_dist_data[
                  i
                ].target_distribution = this.target_distribution;
                this.target_yr_dist_data[
                  i
                ].budget_requirement = this.target_budget_requirement;
                this.target_yr_dist_data[
                  i
                ].success_indicator = this.target_success_indicator;

                this.target_yr_dist_data[i].tagged = this.tagged_head_data;
                this.tagged_head_data = [];
                this.yrSelected = null;
                this.closeTargetDialog();
              } else {
                let tempTotal = 0;
                for (let j = 0; j < this.tagged_head_data.length; j++) {
                  tempTotal += parseInt(this.tagged_head_data[j].target);
                }

                if (tempTotal >= target) {
                  this.target_yr_dist_data[
                    i
                  ].target_distribution = this.target_distribution;
                  this.target_yr_dist_data[
                    i
                  ].budget_requirement = this.target_budget_requirement;

                  this.target_yr_dist_data[
                    i
                  ].success_indicator = this.target_success_indicator;
                  this.target_yr_dist_data[i].tagged = this.tagged_head_data;

                  this.tagged_head_data = [];
                  this.yrSelected = null;
                  this.closeTargetDialog();
                } else {
                  this.fadeAwayMessage.show = true;
                  this.fadeAwayMessage.type = "error";
                  this.fadeAwayMessage.header = "System Message";
                  this.fadeAwayMessage.message =
                    "Target should be higher or equal to " + target + "!";
                }
              }
            }
          }

          this.targetBtnLoading = false;
        }
      } else {
        this.targetBtnLoading = false;
      }
    },

    closeTargetDialog() {
      this.target_desc = null;
      this.target_val = null;
      this.target_budget_requirement = null;
      this.tagged_head_data = [];
      this.targetInnerAction = null;
      this.targetDialog = false;
    },

    setTargetDistribution() {
      // sameTargetDistYearList
      this.getHeadUsers();
      if (this.isSameTargetDistribution) {
        if (
          this.pap_sdp_target !== null &&
          this.pap_sdp_target !== "" &&
          this.pap_baseline !== null &&
          this.pap_baseline !== ""
        ) {
          this.sameTargetDistYearList = [];
          for (let i = 0; i < this.target_yr_dist_data.length; i++) {
            this.sameTargetDistYearList.push({
              year: this.target_yr_dist_data[i].year,
              checked: this.target_yr_dist_data[i].target_distribution
                ? true
                : false,
            });
          }

          this.target_distribution = null;
          this.target_budget_requirement = null;
          this.targetInnerAction = "Update";
          this.tagged_head_data = [];
          this.targetDialog = true;
        } else {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.header = "System Message";
          this.fadeAwayMessage.message =
            "Please provide Baseline and SDP Target!";
          setTimeout(() => {
            this.isSameTargetDistribution = false;
          }, 500);
        }
      } else {
        for (let i = 0; i < this.target_yr_dist_data.length; i++) {
          this.target_yr_dist_data[i].target_distribution = null;
          this.target_yr_dist_data[i].budget_requirement = null;
          this.target_yr_dist_data[i].success_indicator = null;
          this.target_yr_dist_data[i].tagged = [];
        }
      }
    },

    add_head_tagging() {
      this.tagHeadAction = "Add";
      let list = this.usersList;
      if (this.tagged_head_data.length > 0) {
        for (let i = 0; i < this.tagged_head_data.length; i++) {
          for (let j = 0; j < list.length; j++) {
            if (this.tagged_head_data[i].head_user.usID == list[j].usID) {
              list.splice(j, 1);
              break;
            }
          }
        }
        this.tempHeadsList = list;
      } else {
        this.tempHeadsList = this.usersList;
      }
      console.log("VPID TYPE", typeof this.vpID);
      this.head_target = null;
      this.tagged_head_temp_data = !this.isDistributed ? this.vpID : null;

      this.headsDialog = true;
    },

    removeTaggedOnClick(item) {
      console.log("CLOSING", item, this.tagged_head_data);
      this.tagged_head_temp_data = null;

      for (let i = 0; i < this.tagged_head_data.length; i++) {
        if (this.tagged_head_data[i].head_user.usID == item.head_user.usID) {
          if (!item.id) {
            this.tagged_head_data.splice(i, 1);
          } else {
            if (item.id == this.tagged_head_data[i].id) {
              this.untagList.push(this.tagged_head_data[i]);
              this.tagged_head_data.splice(i, 1);
            }
          }
        }
      }
    },

    edit_tag(item, index) {
      this.getHeadUsers();
      console.log(item, index);

      // let tempTaggedHeads = this.tagged_head_data;
      // for (let i = 0; i < tempTaggedHeads.length; i++) {
      //   if (tempTaggedHeads[i].head_user.usID != item.head_user.usID) {
      //     tempTaggedHeads.splice(i, 1);
      //   }
      // }

      // console.log("TEMPHEAD", tempTaggedHeads);
      setTimeout(() => {
        let tempTaggedHeads = [];
        for (let i = 0; i < this.tagged_head_data.length; i++) {
          if (this.tagged_head_data[i].head_user.usID != item.head_user.usID) {
            tempTaggedHeads.push(this.tagged_head_data[i]);
          }
        }
        this.tagIdxToUpdate = index;
        this.tagHeadAction = "Update";
        let list = this.usersList;
        if (tempTaggedHeads.length > 0) {
          for (let i = 0; i < tempTaggedHeads.length; i++) {
            for (let j = 0; j < list.length; j++) {
              if (tempTaggedHeads[i].head_user.usID == list[j].usID) {
                list.splice(j, 1);
                break;
              }
            }
          }
          this.tempHeadsList = list;
        }
        // this.tempHeadsList = list;
        this.head_target = item.target;
        if (typeof item.head_user.usID == "number") {
          this.tagged_head_temp_data = item.head_user.usID.toString();
        } else {
          this.tagged_head_temp_data = item.head_user.usID;
        }

        this.headsDialog = true;
      }, 100);
    },

    untag(item) {
      this.unTagUser = item;
      this.unTagDialog = true;
    },

    confirmUntag() {
      // console.log(this.unTagUser);
      this.removeTaggedOnClick(this.unTagUser);
      this.unTagUser = null;
      this.unTagDialog = false;
    },
    cancelUntag() {
      this.unTagUser = null;
      this.unTagDialog = false;
    },

    closeHeadsDialog() {
      this.tagHeadAction = null;
      this.headsDialog = false;
    },

    saveTaggedHeads() {
      if (this.pap_sdp_target_type != "consistent") {
        for (let j = 0; j < this.usersList.length; j++) {
          if (this.tagged_head_temp_data == this.usersList[j].usID) {
            this.tagged_head_data.push({
              head_user: this.usersList[j],
              target: this.head_target,
            });
            break;
          }
        }
      } else {
        const headTarget = Number(this.head_target);
        const sdpTarget = Number(this.pap_sdp_target);
        let tempT =
          parseInt(this.getConsitentTotalTargets.total) +
          parseInt(this.head_target);
        if (this.getConsitentTotalTargets.total <= this.pap_sdp_target) {
          if (this.isTargetSameDistribution == true) {
            if (headTarget > sdpTarget) {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message =
                "Total tagged targets exceeded the SDP Target!";
              return;
            }
            for (let j = 0; j < this.usersList.length; j++) {
              this.tagged_head_data.push({
                head_user: this.usersList[j],
                target: this.head_target,
              });
              break;
            }
          } else {
            if (tempT <= this.pap_sdp_target) {
              for (let j = 0; j < this.usersList.length; j++) {
                if (this.tagged_head_temp_data == this.usersList[j].usID) {
                  this.tagged_head_data.push({
                    head_user: this.usersList[j],
                    target: this.head_target,
                  });
                  break;
                }
              }
            } else {
              this.fadeAwayMessage.show = true;
              this.fadeAwayMessage.type = "error";
              this.fadeAwayMessage.header = "System Message";
              this.fadeAwayMessage.message =
                "Total tagged targets exceeded the SDP Target!";
            }
          }
        } else if (
          this.isTargetSameDistribution == true &&
          this.pap_sdp_target_type == "consistent"
        ) {
          if (headTarget > sdpTarget) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message =
              "Total tagged targets exceeded the SDP Target!";

            return;
          }
          for (let j = 0; j < this.usersList.length; j++) {
            this.tagged_head_data.push({
              head_user: this.usersList[j],
              target: this.head_target,
            });
            break;
          }
        } else {
          this.fadeAwayMessage.show = true;
          this.fadeAwayMessage.type = "error";
          this.fadeAwayMessage.header = "System Message";
          this.fadeAwayMessage.message =
            "Total tagged targets exceeded the SDP Target!";
        }
      }

      this.closeHeadsDialog();
    },

    updateTaggedHeads() {
      for (let j = 0; j < this.usersList.length; j++) {
        if (this.tagged_head_temp_data == this.usersList[j].usID) {
          this.tagged_head_data[this.tagIdxToUpdate].head_user = this.usersList[
            j
          ];
          this.tagged_head_data[this.tagIdxToUpdate].target = this.head_target;
          break;
        }
      }
      this.tagIdxToUpdate = null;
      this.closeHeadsDialog();
    },

    addnew() {
      if (this.$refs.PAPForm.validate()) {
        this.isSubmitLoading = true;
        let data = {
          strat_planID: this.data.strat_planID,
          goalID: this.data.goalID,
          strategicOutcomeID: this.data.strategicOutcomeID,
          strategyID: this.data.strategyID,
          code: this.pap_code.slice(0, -1),
          indicators: this.pap_indicators,
          def_specs: this.pap_def_specs,
          baseline: this.pap_baseline,
          sdp_target: this.pap_sdp_target,
          sdp_target_type: this.pap_sdp_target_type,
          sdp_target_format: this.pap_sdp_target_format,
          isSameTargetDistribution: this.isSameTargetDistribution,
          isTargetSameDistribution: this.isTargetSameDistribution,
          tagged: JSON.stringify(this.target_yr_dist_data),
          isDistributed: this.isDistributed,
        };
        // console.log(data);

        this.axiosCall("/pap", "POST", data).then((res) => {
          console.log(res);
          if (res.data.status == 201) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
            this.isSubmitLoading = false;
            this.closeD();
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
          this.confirmDialog = false;
        });
      }
    },

    update() {
      if (this.$refs.PAPForm.validate()) {
        this.isSubmitLoading = true;
        let data = {
          strat_planID: this.data.strat_planID,
          goalID: this.data.goalID,
          strategicOutcomeID: this.data.strategicOutcomeID,
          strategyID: this.data.strategyID,
          code: this.pap_code.slice(0, -1),
          indicators: this.pap_indicators,
          def_specs: this.pap_def_specs,
          baseline: this.pap_baseline,
          sdp_target: this.pap_sdp_target,
          sdp_target_type: this.pap_sdp_target_type,
          sdp_target_format: this.pap_sdp_target_format,
          isSameTargetDistribution: this.isSameTargetDistribution,
          isTargetSameDistribution: this.isTargetSameDistribution,
          tagged: JSON.stringify(this.target_yr_dist_data),
          untagged_data: JSON.stringify(this.untagList),
          isDistributed: this.isDistributed,
        };
        // console.log(data);
        this.isSubmitLoading = false;
        this.axiosCall("/pap/" + this.id, "PATCH", data).then((res) => {
          console.log(res);
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;

            this.closeD();
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
          this.confirmDialog = false;
        });
      }
    },

    upsert() {
      if (this.$refs.PAPForm.validate()) {
        this.isSubmitLoading = true;
        let data = {
          strat_planID: this.data.strat_planID,
          goalID: this.data.goalID,
          strategicOutcomeID: this.data.strategicOutcomeID,
          strategyID: this.data.strategyID,
          code: this.pap_code,
          indicators: this.pap_indicators,
          def_specs: this.pap_def_specs,
          baseline: this.pap_baseline,
          sdp_target: this.pap_sdp_target,
          sdp_target_type: this.pap_sdp_target_type,
          sdp_target_format: this.pap_sdp_target_format,
          isSameTargetDistribution: this.isSameTargetDistribution,
          isTargetSameDistribution: this.isTargetSameDistribution,
          tagged: JSON.stringify(this.target_yr_dist_data),
          untagged_data: JSON.stringify(this.untagList),
          isDistributed: this.isDistributed,
          hasSameCode: true,
        };
        // console.log(data);
        this.isSubmitLoading = false;
        this.axiosCall("/pap/upsert/" + this.id, "PATCH", data).then((res) => {
          console.log(res);
          if (res.data.status == 200) {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "success";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;

            this.closeD();
          } else {
            this.fadeAwayMessage.show = true;
            this.fadeAwayMessage.type = "error";
            this.fadeAwayMessage.header = "System Message";
            this.fadeAwayMessage.message = res.data.msg;
          }
          this.confirmDialog = false;
        });
      }
    },

    getEmpDetails() {
      this.axiosCall("/employee/my-details", "GET").then((res) => {
        console.log(res.data);
        this.assigned_office = res.data.officeID;
      });
    },

    loadYearly(id) {
      this.axiosCall("/strategic-planning/" + id, "GET").then((res) => {
        console.log("STRAT", res.data);
        let from = res.data.yearFrom;
        let to = res.data.yearTo;
        for (let i = from; i <= to; i++) {
          this.target_yr_dist_data.push({
            year: i,
            target_distribution: null,
            budget_requirement: null,
            success_indicator: null,
            tagged: [],
          });
        }

        // this.officeList = res.data;
      });
    },

    loadYearlyTagged(id) {
      this.axiosCall("/pap/loadYearlyTagged/" + id, "GET").then((res) => {
        console.log("STRATssss", res.data);
        this.target_yr_dist_data = res.data;
        // this.officeList = res.data;
      });
    },

    getHeadUsers() {
      this.axiosCall(
        "/user-details/getUsersHeadProgChairAbove_Office",
        "GET",
      ).then((res) => {
        let data = res.data;
        let data1 = [];
        data.forEach((element, i) => {
          data[i].name = this.toTitleCase(element.name);
        });

        for (let i = 0; i < data.length; i++) {
          if (data[i].roleID == 6) {
            data1.push(data[i]);
          } else {
            if (data[i].roleID == 7 && data[i].office_underID == 1) {
              data1.push(data[i]);
            }
          }
        }

        this.usersList = data1;
      });
    },

    getVpAssignedByGoalID(id) {
      this.axiosCall(
        "/strategic-planning-goal/getVpAssignedByGoalID/" + id,
        "GET",
      ).then((res) => {
        this.vpID = res.data.usID;
      });
    },

    getDesignations() {
      this.axiosCall("/designations", "GET").then((res) => {
        this.designationList = res.data;
      });
    },
  },
};
</script>

<style scoped>
#table_dist {
  width: 100%;
  border-collapse: collapse;
}
#table_dist th td {
  border: 1px solid grey;
}
#table_dist th,
#table_dist td {
  padding: 5px;
}
#table_dist th {
  text-align: center;
}

.justify-center::v-deep .v-input--radio-group__input {
  justify-content: space-around;
}
.baseline-card {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #f8fbf9;
  border: 1px solid #dce9e2;
  border-radius: 8px;
}

.baseline-label {
  font-size: 14px;
  font-weight: 600;
  color: #616161;
}

.baseline-value {
  font-size: 14px;
  font-weight: 700;
  color: #147452;
}
</style>
